import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@trillion/ui/components/ui/button";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { NetworkForm } from "@/components/networks/network-form";
import { adminApi } from "@/lib/admin-api";
import { describeApiError } from "@/lib/api-error";
import type { ProductSource } from "@/types";

export function NetworkEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sourceId = Number(id);

  const [network, setNetwork] = useState<ProductSource | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchNetwork() {
      try {
        const data = await adminApi.sources.get(sourceId);
        setNetwork(data);
      } catch (err) {
        toast.error("Failed to load network", {
          description: describeApiError(err),
        });
        navigate("/networks");
      } finally {
        setIsLoading(false);
      }
    }
    fetchNetwork();
  }, [sourceId, navigate]);

  async function handleSubmit(data: Partial<ProductSource>) {
    setIsSaving(true);
    try {
      await adminApi.sources.update(sourceId, data);
      toast.success("Network updated successfully");
      navigate("/networks");
    } catch (err) {
      toast.error("Failed to update network", {
        description: describeApiError(err),
      });
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-6 py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!network) return null;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={`Edit: ${network.name}`}
        description="Update network details"
        actions={
          <Button variant="outline" onClick={() => navigate("/networks")}>
            <ArrowLeft className="mr-2 size-4" />
            Back to Networks
          </Button>
        }
      />
      <NetworkForm initialData={network} onSubmit={handleSubmit} isLoading={isSaving} />
    </div>
  );
}
